/**
 * <PageBackground /> — مُركَّب مرّة واحدة في SiteShell.
 * يكتشف pageKey من pathname + device من viewport + market من LocaleContext،
 * ويختار variant الأنسب بترتيب: device:market → device:all → all:market → all:all.
 *
 * يدعم وضعَين:
 *   - mode="fixed"          → خلفيّة ثابتة تملأ الشاشة (الافتراضيّ).
 *   - mode="parallax_tall"  → تُعرض بحجم شاشتين (200vh) مع parallax بسرعة نصف التمرير.
 */
import * as React from "react";
import { useLocation } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  resolveVariant,
  makeVariantKey,
  type PageKey,
  type MarketKey,
} from "@/utils/pageBackgrounds.shared";
import { getPageBackgrounds } from "@/utils/pageBackgrounds.functions";
import { getNewProductPages } from "@/utils/newProductBlocks.functions";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLocale } from "@/contexts/LocaleContext";
import { ImageHotspots } from "@/components/ImageHotspots";

function pageKeyFromPathname(pathname: string): PageKey | null {
  if (pathname === "/") return "home";
  if (pathname === "/new-product" || pathname.startsWith("/new-product/")) return "new-product";

  if (pathname === "/account/wishlist") return "wishlist";
  if (pathname === "/about" || pathname.startsWith("/about/")) return "about";
  if (pathname === "/journal" || pathname.startsWith("/journal/")) return "journal";
  if (pathname === "/contact") return "contact";
  if (pathname === "/gifts" || pathname.startsWith("/gifts/")) return "gifts";
  if (pathname === "/ambassadors" || pathname.startsWith("/ambassadors/")) return "ambassadors";
  if (pathname === "/collection/gift-set-perfume-collection") return "collection-large";
  if (pathname === "/collection/ladybag-collection") return "collection-ladybag";

  if (pathname.startsWith("/product/")) return "product";
  if (
    pathname === "/shop" ||
    pathname.startsWith("/shop/") ||
    
    pathname.startsWith("/collection/")
  )
    return "shop";
  if (pathname === "/account" || pathname.startsWith("/account/")) return "account";
  return null;
}

export function PageBackground() {
  const { pathname } = useLocation();
  const pageKey = pageKeyFromPathname(pathname);
  const isMobile = useIsMobile();
  const { effectiveMarket } = useLocale();
  const fn = useServerFn(getPageBackgrounds);
  const { data } = useQuery({
    queryKey: ["page-backgrounds"],
    queryFn: () => fn(),
    staleTime: 5 * 60_000,
    enabled: pageKey !== null,
  });

  // على `/new-product` بلا slug: الصفحة تعرض المنتج الوحيد مباشرةً،
  // فنجلب القائمة لنستخرج slug الوحيد ونطبّق خلفيّته المخصّصة.
  const isNewProductIndex = /^\/new-product\/?$/i.test(pathname);
  const pagesFn = useServerFn(getNewProductPages);
  const { data: npPages } = useQuery({
    queryKey: ["new-product-pages"],
    queryFn: () => pagesFn(),
    staleTime: 60_000,
    enabled: isNewProductIndex,
  });

  // مفتاح خاصّ لكلّ صفحة «منتج جديد»: `new-product:<slug>` يتقدّم على المفتاح العامّ.
  const scopedKey = React.useMemo(() => {
    const m = pathname.match(/^\/new-product\/([a-z0-9-]+)\/?$/i);
    if (m) return `new-product:${m[1].toLowerCase()}`;
    if (isNewProductIndex && npPages?.pages?.length === 1) {
      return `new-product:${npPages.pages[0].slug.toLowerCase()}`;
    }
    return null;
  }, [pathname, isNewProductIndex, npPages]);

  // الخلفيّة تظهر دائماً خلف المحتوى، بما في ذلك أسفل الهيرو عند اختيار
  // ارتفاع أقصر من كامل الشاشة. الهيرو يبقى فوقها بفضل ترتيب z-index.
  const bgDevice = isMobile ? "mobile" : "desktop";
  const bgMarket = effectiveMarket as Exclude<MarketKey, "all">;
  const bg = pageKey && data
    ? (scopedKey
        ? resolveVariant((data.map as Record<string, typeof data.map[PageKey]>)[scopedKey], bgDevice, bgMarket)
        : null) ?? resolveVariant(data.map[pageKey], bgDevice, bgMarket)
    : null;




  // كشف تفضيلات الحركة المخفَّضة + الشبكات الضعيفة لتعطيل parallax تلقائيّاً
  const [allowParallax, setAllowParallax] = React.useState(true);
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const nav = navigator as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean; addEventListener?: (t: "change", l: () => void) => void; removeEventListener?: (t: "change", l: () => void) => void };
    };
    const conn = nav.connection;
    const evaluate = () => {
      const slow = !!conn && (conn.saveData || conn.effectiveType === "slow-2g" || conn.effectiveType === "2g" || (conn.effectiveType as string) === "slow-3g");
      setAllowParallax(!mql.matches && !slow);
    };
    evaluate();
    mql.addEventListener?.("change", evaluate);
    conn?.addEventListener?.("change", evaluate);
    return () => {
      mql.removeEventListener?.("change", evaluate);
      conn?.removeEventListener?.("change", evaluate);
    };
  }, []);

  const isParallax = bg?.mode === "parallax_tall" && allowParallax;
  const shellMinHeight = bg?.mode === "parallax_tall" ? "200vh" : "100vh";
  const tailHeight = bg?.mode === "parallax_tall" ? "100vh" : "0px";
  const imgRef = React.useRef<HTMLImageElement>(null);

  React.useEffect(() => {
    document.documentElement.style.setProperty("--page-bg-shell-min-height", shellMinHeight);
    document.documentElement.style.setProperty("--page-bg-tail-height", tailHeight);
    return () => {
      document.documentElement.style.removeProperty("--page-bg-shell-min-height");
      document.documentElement.style.removeProperty("--page-bg-tail-height");
    };
  }, [shellMinHeight, tailHeight]);

  // parallax عبر rAF + كتابة مباشرة على transform (بدون setState) لتفادي re-renders
  React.useEffect(() => {
    if (!isParallax) return;
    let raf = 0;
    let pending = false;
    const apply = () => {
      pending = false;
      const el = imgRef.current;
      if (!el) return;
      const y = window.scrollY * 0.5;
      el.style.transform = `translate3d(0, ${-y}px, 0)`;
    };
    const onScroll = () => {
      if (pending) return;
      pending = true;
      raf = window.requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [isParallax]);

  if (!bg?.url && !bg?.color) return null;

  // لون خلفيّة فقط (بلا صورة) — طبقة ثابتة تملأ الشاشة
  if (!bg.url && bg.color) {
    return (
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0"
        style={{ backgroundColor: bg.color }}
      />
    );
  }

  // مفتاح النقاط = pageKey:device:market — كلّ variant له نقاطه الخاصّة
  const device: "mobile" | "desktop" = isMobile ? "mobile" : "desktop";
  const hotspotsScopeId = pageKey
    ? `${pageKey}:${makeVariantKey(device, effectiveMarket as Exclude<MarketKey, "all">)}`
    : null;

  if (bg.mode === "parallax_tall") {
    return (
      <>
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-x-0 top-0 z-0 overflow-hidden"
          style={{ height: "100vh", contain: "layout paint size", backgroundColor: bg.color || undefined }}
        >
          <img
            ref={imgRef}
            src={bg.url}
            alt=""
            loading="eager"
            decoding="async"
            fetchPriority="low"
            className="absolute inset-x-0 top-0 w-full h-auto select-none"
            style={{
              willChange: isParallax ? "transform" : "auto",
              transform: "translate3d(0,0,0)",
              backfaceVisibility: "hidden",
              objectFit: "cover",
              objectPosition: `${bg.posX}% ${bg.posY}%`,
            }}
            draggable={false}
          />
        </div>
        {hotspotsScopeId && (
          <div className="pointer-events-none fixed inset-x-0 top-0 z-30" style={{ height: "100vh" }}>
            <ImageHotspots scope="page_background" scopeId={hotspotsScopeId} />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundColor: bg.color || undefined,
          backgroundImage: `url('${bg.url}')`,
          backgroundSize: bg.size,
          backgroundPosition: `${bg.posX}% ${bg.posY}%`,
          backgroundRepeat: "no-repeat",
        }}
      />
      {hotspotsScopeId && (
        <div className="pointer-events-none fixed inset-0 z-30">
          <ImageHotspots scope="page_background" scopeId={hotspotsScopeId} />
        </div>
      )}
    </>
  );
}
